import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useToast } from "../components/ui/Toast";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

const API = process.env.REACT_APP_API_URL;

const STATUS_LABELS = {
  pending: { label: "Non planifiée", color: "bg-slate-500/20 text-slate-300 border-slate-500/30" },
  scheduled: { label: "Planifiée", color: "bg-amber-500/20 text-amber-300 border-amber-500/30" },
  active: { label: "Active", color: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30" },
  in_progress: { label: "En cours", color: "bg-blue-500/20 text-blue-300 border-blue-500/30" },
  used: { label: "Utilisée", color: "bg-indigo-500/20 text-indigo-300 border-indigo-500/30" },
  expired: { label: "Expirée", color: "bg-red-500/20 text-red-300 border-red-500/30" },
  refunded: { label: "Remboursée", color: "bg-rose-500/20 text-rose-300 border-rose-500/30" },
  completed: { label: "Complétée", color: "bg-purple-500/20 text-purple-300 border-purple-500/30" },
};

function StatusBadge({ status }) {
  const s = STATUS_LABELS[status] || { label: status, color: "bg-gray-500/20 text-gray-300 border-gray-500/30" };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${s.color}`}>
      {s.label}
    </span>
  );
}

function Card({ title, children, action }) {
  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
      <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-white">{title}</h3>
        {action}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-4 py-1.5 border-b border-white/5 last:border-0">
      <span className="text-xs text-gray-500 shrink-0">{label}</span>
      <span className="text-xs text-gray-200 text-right font-mono break-all">{value ?? "—"}</span>
    </div>
  );
}

// Rendu récursif d'un JSON avec mise en forme
function JsonTree({ data, depth = 0 }) {
  if (data === null || data === undefined) return <span className="text-gray-500">null</span>;
  if (typeof data === "boolean") return <span className={data ? "text-emerald-400" : "text-red-400"}>{String(data)}</span>;
  if (typeof data === "number") return <span className="text-amber-300">{data}</span>;
  if (typeof data === "string") return <span className="text-sky-300">"{data}"</span>;
  if (Array.isArray(data)) {
    if (data.length === 0) return <span className="text-gray-500">[]</span>;
    return (
      <div className="pl-3 border-l border-white/10">
        {data.map((item, i) => (
          <div key={i} className="py-0.5">
            <span className="text-gray-500 text-xs">{i}: </span>
            <JsonTree data={item} depth={depth + 1} />
          </div>
        ))}
      </div>
    );
  }
  if (typeof data === "object") {
    return (
      <div className={depth > 0 ? "pl-3 border-l border-white/10" : ""}>
        {Object.entries(data).map(([k, v]) => (
          <div key={k} className="py-0.5">
            <span className="text-indigo-300 text-xs font-semibold">{k}: </span>
            <JsonTree data={v} depth={depth + 1} />
          </div>
        ))}
      </div>
    );
  }
  return <span className="text-gray-300">{String(data)}</span>;
}

export default function AdminSessionDetail() {
  const { code } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const token = localStorage.getItem("adminToken");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState(null);

  // InfraState editor
  const [editingInfra, setEditingInfra] = useState(false);
  const [infraText, setInfraText] = useState("");
  const [infraError, setInfraError] = useState("");

  // Reschedule
  const [rescheduleDate, setRescheduleDate] = useState("");
  const [showReschedule, setShowReschedule] = useState(false);

  // Mission guide filter
  const [missionStageFilter, setMissionStageFilter] = useState(0); // 0 = tous

  const intervalRef = useRef(null);

  useEffect(() => {
    if (!token) { navigate("/admin/login"); return; }
  }, [token, navigate]);

  const fetchDetail = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/api/admin/sessions/${code}/detail`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(d);
    } catch (err) {
      if (err?.response?.status === 404) {
        toast.error("Session introuvable");
        navigate("/admin/dashboard");
      }
    } finally {
      setLoading(false);
    }
  }, [code, token, navigate]);

  useEffect(() => {
    fetchDetail();
    // Polling toutes les 5s si session en cours
    intervalRef.current = setInterval(fetchDetail, 5000);
    return () => clearInterval(intervalRef.current);
  }, [fetchDetail]);

  const handleStageChange = async (stage) => {
    try {
      await axios.patch(`${API}/api/admin/sessions/${code}/stage`, { stage }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Stage → S${stage}`);
      fetchDetail();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Erreur");
    }
    setConfirm(null);
  };

  const handleReset = async () => {
    try {
      await axios.post(`${API}/api/admin/sessions/${code}/reset`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Session réinitialisée");
      fetchDetail();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Erreur reset");
    }
    setConfirm(null);
  };

  const handleResetTraining = async () => {
    try {
      await axios.delete(`${API}/api/admin/sessions/${code}/training`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("TrainingSession réinitialisée");
      fetchDetail();
    } catch (err) {
      toast.error("Erreur reset training");
    }
    setConfirm(null);
  };

  const handleReschedule = async () => {
    if (!rescheduleDate) { toast.error("Date requise"); return; }
    try {
      await axios.patch(`${API}/api/admin/sessions/${code}/reschedule`, { date: rescheduleDate }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Session reportée");
      setShowReschedule(false);
      fetchDetail();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Erreur report");
    }
  };

  const handleResetInfra = async () => {
    try {
      await axios.patch(`${API}/api/admin/sessions/${code}/infrastate`, { infraState: null }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("InfraState remis à null ✅");
      fetchDetail();
    } catch {
      toast.error("Erreur reset infraState");
    }
    setConfirm(null);
  };

  const handleSaveInfra = async () => {
    setInfraError("");
    let parsed;
    try {
      parsed = JSON.parse(infraText);
    } catch {
      setInfraError("JSON invalide");
      return;
    }
    try {
      await axios.patch(`${API}/api/admin/sessions/${code}/infrastate`, { infraState: parsed }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("InfraState sauvegardé");
      setEditingInfra(false);
      fetchDetail();
    } catch (err) {
      toast.error("Erreur sauvegarde infraState");
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-800 flex items-center justify-center text-gray-400 text-sm">
      Chargement…
    </div>
  );

  if (!data) return null;

  const { session, players, missionGuide, scenarioProgress, trainingSession, metrics } = data;

  const isLive = session.status === "in_progress";

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-800 text-white">
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-5">

        {/* Header */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <button
              onClick={() => navigate("/admin/dashboard")}
              className="text-xs text-gray-500 hover:text-gray-300 mb-2 flex items-center gap-1 transition"
            >
              ← Dashboard
            </button>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-mono text-xl font-bold text-indigo-300">{code}</h1>
              <StatusBadge status={session.status} />
              {isLive && (
                <span className="flex items-center gap-1.5 text-xs text-emerald-400">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  Live
                </span>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">{session.company?.name} · {session.packType === "extended" ? "Semaine" : "Journée"}</p>
          </div>
          <button
            onClick={() => { localStorage.removeItem("adminToken"); navigate("/admin/login"); }}
            className="px-3 py-1.5 bg-gray-800 hover:bg-gray-700 border border-white/10 rounded-lg text-xs transition"
          >
            Déconnexion
          </button>
        </div>

        {/* Infos de base */}
        <Card title="📋 Informations">
          <div className="space-y-0.5">
            <InfoRow label="Code" value={session.code} />
            <InfoRow label="Statut" value={session.status} />
            <InfoRow label="Stage actuel" value={`S${session.currentStage}`} />
            <InfoRow label="Lancée" value={session.launched ? "Oui" : "Non"} />
            <InfoRow label="Date début" value={session.startAt ? format(new Date(session.startAt), "dd MMM yyyy HH:mm", { locale: fr }) : null} />
            <InfoRow label="Date fin" value={session.endAt ? format(new Date(session.endAt), "dd MMM yyyy HH:mm", { locale: fr }) : null} />
            <InfoRow label="Démarrée à" value={session.startedAt ? format(new Date(session.startedAt), "dd MMM yyyy HH:mm", { locale: fr }) : null} />
            <InfoRow label="Terminée à" value={session.finishedAt ? format(new Date(session.finishedAt), "dd MMM yyyy HH:mm", { locale: fr }) : null} />
            <InfoRow label="Durée (s)" value={session.duration} />
            <InfoRow label="Équipe" value={session.teamName} />
            <InfoRow label="Points" value={session.points} />
            <InfoRow label="Entreprise" value={`${session.company?.name} (${session.company?.email})`} />
          </div>
        </Card>

        {/* Actions */}
        <Card title="⚡ Actions">
          <div className="space-y-4">

            {/* Changer stage */}
            <div>
              <p className="text-xs text-gray-400 mb-2">Changer de stage</p>
              <div className="flex gap-2">
                {[1, 2, 3].map(s => (
                  confirm?.type === 'stage' && confirm?.value === s ? (
                    <div key={s} className="flex gap-1 items-center">
                      <span className="text-xs text-amber-300">S{s} ?</span>
                      <button onClick={() => handleStageChange(s)} className="px-2 py-1 bg-amber-600 hover:bg-amber-500 rounded text-xs">Oui</button>
                      <button onClick={() => setConfirm(null)} className="px-2 py-1 bg-gray-700 rounded text-xs">Non</button>
                    </div>
                  ) : (
                    <button
                      key={s}
                      onClick={() => setConfirm({ type: 'stage', value: s })}
                      className={`px-4 py-2 rounded-lg text-sm font-semibold border transition ${
                        session.currentStage === s
                          ? 'bg-indigo-600 border-indigo-500 text-white'
                          : 'bg-white/5 border-white/10 text-gray-300 hover:bg-white/10'
                      }`}
                    >
                      S{s}
                    </button>
                  )
                ))}
              </div>
            </div>

            {/* Reporter */}
            <div>
              <p className="text-xs text-gray-400 mb-2">Reporter la session</p>
              {showReschedule ? (
                <div className="flex gap-2 items-center flex-wrap">
                  <input
                    type="date"
                    value={rescheduleDate}
                    onChange={e => setRescheduleDate(e.target.value)}
                    className="px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-sm text-white"
                  />
                  <button onClick={handleReschedule} className="px-3 py-2 bg-amber-600 hover:bg-amber-500 rounded-lg text-sm">OK</button>
                  <button onClick={() => setShowReschedule(false)} className="px-3 py-2 bg-gray-700 rounded-lg text-sm">Annuler</button>
                </div>
              ) : (
                <button onClick={() => setShowReschedule(true)} className="px-4 py-2 bg-amber-600/30 hover:bg-amber-600/50 border border-amber-500/30 rounded-lg text-sm text-amber-300 transition">
                  📅 Reporter
                </button>
              )}
            </div>

            {/* Reset TrainingSession */}
            {trainingSession && (
              <div>
                <p className="text-xs text-gray-400 mb-2">Formation (Stage 2)</p>
                {confirm?.type === 'training' ? (
                  <div className="flex gap-1 items-center">
                    <span className="text-xs text-orange-300">Confirmer reset training ?</span>
                    <button onClick={handleResetTraining} className="px-2 py-1 bg-orange-600 hover:bg-orange-500 rounded text-xs">Oui</button>
                    <button onClick={() => setConfirm(null)} className="px-2 py-1 bg-gray-700 rounded text-xs">Non</button>
                  </div>
                ) : (
                  <button onClick={() => setConfirm({ type: 'training' })} className="px-4 py-2 bg-orange-600/30 hover:bg-orange-600/50 border border-orange-500/30 rounded-lg text-sm text-orange-300 transition">
                    🔄 Reset TrainingSession
                  </button>
                )}
              </div>
            )}

            {/* Reset complet */}
            <div>
              <p className="text-xs text-gray-400 mb-2">Reset complet</p>
              {confirm?.type === 'reset' ? (
                <div className="flex gap-1 items-center">
                  <span className="text-xs text-red-300">Tout sera purgé. Confirmer ?</span>
                  <button onClick={handleReset} className="px-2 py-1 bg-red-600 hover:bg-red-500 rounded text-xs">Oui</button>
                  <button onClick={() => setConfirm(null)} className="px-2 py-1 bg-gray-700 rounded text-xs">Non</button>
                </div>
              ) : (
                <button onClick={() => setConfirm({ type: 'reset' })} className="px-4 py-2 bg-red-600/30 hover:bg-red-600/50 border border-red-500/30 rounded-lg text-sm text-red-300 transition">
                  🗑 Reset complet
                </button>
              )}
            </div>
          </div>
        </Card>

        {/* Joueurs */}
        <Card title={`👥 Joueurs (${players.length})`}>
          {players.length === 0 ? (
            <p className="text-xs text-gray-500 italic">Aucun joueur connecté</p>
          ) : (
            <div className="space-y-2">
              {players.map(p => (
                <div key={p.id} className="rounded-xl bg-black/20 border border-white/5 px-4 py-3 flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <p className="text-sm font-semibold text-white">{p.pseudo}</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Salle J{p.pageVariant} · Stage vu : S{p.lastStageSeen ?? "?"} · {p.avatarLabel || "sans avatar"}
                    </p>
                  </div>
                  <div className="flex gap-2 flex-wrap text-xs">
                    {p.isReady && <span className="px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">Prêt</span>}
                    {p.handRaised && <span className="px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/30">✋ Main levée</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* InfraState */}
        <Card
          title="🔧 InfraState"
          action={
            !editingInfra ? (
              <div className="flex gap-2">
                {session.infraState !== null && (
                  confirm?.type === 'resetInfra' ? (
                    <div className="flex gap-1 items-center">
                      <span className="text-xs text-red-300">Reset infra ?</span>
                      <button onClick={handleResetInfra} className="px-2 py-1 bg-red-600 hover:bg-red-500 rounded text-xs">Oui</button>
                      <button onClick={() => setConfirm(null)} className="px-2 py-1 bg-gray-700 rounded text-xs">Non</button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setConfirm({ type: 'resetInfra' })}
                      className="px-3 py-1 bg-red-600/30 hover:bg-red-600/50 border border-red-500/30 rounded text-xs text-red-300 transition"
                    >
                      Reset
                    </button>
                  )
                )}
                <button
                  onClick={() => { setInfraText(JSON.stringify(session.infraState, null, 2)); setEditingInfra(true); setInfraError(""); }}
                  className="px-3 py-1 bg-indigo-600/30 hover:bg-indigo-600/50 border border-indigo-500/30 rounded text-xs text-indigo-300 transition"
                >
                  Modifier
                </button>
              </div>
            ) : (
              <div className="flex gap-2">
                <button onClick={handleSaveInfra} className="px-3 py-1 bg-emerald-600 hover:bg-emerald-500 rounded text-xs">Sauvegarder</button>
                <button onClick={() => { setEditingInfra(false); setInfraError(""); }} className="px-3 py-1 bg-gray-700 rounded text-xs">Annuler</button>
              </div>
            )
          }
        >
          {session.infraState === null ? (
            <p className="text-xs text-gray-500 italic">Pas encore d'infraState (session non lancée)</p>
          ) : editingInfra ? (
            <div className="space-y-2">
              <textarea
                value={infraText}
                onChange={e => setInfraText(e.target.value)}
                rows={16}
                className="w-full px-3 py-2 rounded-lg bg-black/60 border border-white/10 text-xs text-green-300 font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              {infraError && <p className="text-xs text-red-400">{infraError}</p>}
            </div>
          ) : (
            <div className="text-xs font-mono leading-relaxed overflow-x-auto">
              <JsonTree data={session.infraState} />
            </div>
          )}
        </Card>

        {/* Mission Guide */}
        {missionGuide.length > 0 && (
          <Card
            title={`📋 Mission Guide (${missionGuide.length})`}
            action={
              <div className="flex gap-1">
                {[0, 1, 2, 3].map(s => (
                  <button
                    key={s}
                    onClick={() => setMissionStageFilter(s)}
                    className={`px-2 py-0.5 rounded text-xs font-medium transition ${
                      missionStageFilter === s
                        ? 'bg-indigo-600 text-white'
                        : 'bg-white/5 text-gray-400 hover:bg-white/10'
                    }`}
                  >
                    {s === 0 ? 'Tous' : `S${s}`}
                  </button>
                ))}
              </div>
            }
          >
            <div className="space-y-1.5">
              {missionGuide
                .filter(item => missionStageFilter === 0 || item.stage === missionStageFilter)
                .map(item => (
                  <div key={item.id} className={`px-3 py-2 rounded-lg text-xs border flex items-start gap-2 ${
                    item.status === 'resolved'
                      ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-300'
                      : 'bg-white/5 border-white/10 text-gray-300'
                  }`}>
                    <span className="shrink-0">{item.status === 'resolved' ? '✅' : '📍'}</span>
                    <span className="font-mono text-indigo-400 shrink-0">S{item.stage}</span>
                    <span>{item.text}</span>
                  </div>
                ))}
              {missionGuide.filter(item => missionStageFilter === 0 || item.stage === missionStageFilter).length === 0 && (
                <p className="text-xs text-gray-500 italic">Aucun élément pour ce stage.</p>
              )}
            </div>
          </Card>
        )}

        {/* ScenarioProgress */}
        {scenarioProgress && (
          <Card title="🗝 Scenario Progress">
            <div className="text-xs font-mono">
              <JsonTree data={scenarioProgress.unlockedKeys} />
            </div>
          </Card>
        )}

        {/* TrainingSession */}
        {trainingSession && (
          <Card title="🎯 Training Session (Stage 2)">
            <div className="space-y-0.5">
              <InfoRow label="Active" value={trainingSession.active ? "Oui" : "Non"} />
              <InfoRow label="Batch actuel" value={trainingSession.currentBatch} />
              <InfoRow label="Score total" value={trainingSession.totalScore} />
              <InfoRow label="Streak" value={trainingSession.streak} />
              <InfoRow label="Total traité" value={trainingSession.totalProcessed} />
              <InfoRow label="Complétée" value={trainingSession.completedAt ? format(new Date(trainingSession.completedAt), "dd MMM HH:mm", { locale: fr }) : "Non"} />
            </div>
          </Card>
        )}

        {/* Métriques */}
        {metrics.length > 0 && (
          <Card title={`📊 Métriques joueurs (${metrics.length})`}>
            <div className="overflow-x-auto">
              <table className="min-w-full text-xs">
                <thead className="text-gray-500 uppercase">
                  <tr>
                    <th className="py-2 pr-4 text-left">Stage</th>
                    <th className="py-2 pr-4 text-left">Joueur</th>
                    <th className="py-2 pr-4 text-left">Clé</th>
                    <th className="py-2 text-left">Valeur</th>
                  </tr>
                </thead>
                <tbody>
                  {metrics.map(m => {
                    const player = players.find(p => p.id === m.playerId);
                    return (
                      <tr key={m.id} className="border-t border-white/5">
                        <td className="py-1.5 pr-4 text-gray-400">S{m.stage}</td>
                        <td className="py-1.5 pr-4 text-gray-300">{player?.pseudo || `#${m.playerId}`}</td>
                        <td className="py-1.5 pr-4 text-indigo-300 font-mono">{m.metricKey}</td>
                        <td className="py-1.5 text-amber-300">{m.value}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        )}

      </div>
    </div>
  );
}